import Service, { service } from '@ember/service'
import downloadjs from 'downloadjs'
import { join } from 'teacher-ui-client/utils/uri'
import config from 'teacher-ui-client/config/environment'
import { format } from 'date-fns'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type { LanguageCode } from 'teacher-ui-client/components/manage/students/download-parent-letters'

interface FileOptions {
  prefix: string
  mimetype: string
  timestamp?: Date
  timestampFormat?: string
}

export class DownloadFileError extends Error {
  name: string

  message: string

  response?: Response

  originalError?: unknown

  constructor(requestUrl: string | URL, response?: Response, originalError?: unknown) {
    super(typeof originalError === 'string' ? originalError : 'download file error')
    this.name = 'DownloadFileError'
    this.message = `Failed to download file from ${requestUrl.toString()}`
    this.response = response
    this.originalError = originalError
  }
}

export interface ParentLetterParams {
  subscription_type?: SubscriptionType
  student_ids: string[]
  second_language_code?: LanguageCode
}

/**
 * Api to download documents (pdf/zip) from our readingeggs endpoints.
 *
 * @example
 * const params = { subscription_type: 'reading', student_ids: [1, 2, 3] }
 * this.downloadFile.downloadParentLettersForStudents(params)
 *
 * This will initiate a download (browser download) of the parentletters for the given students.
 */
export default class DownloadFileService extends Service {
  @service authToken!: AuthToken

  downloadjs = downloadjs

  // authorization token
  get Authorization() {
    return this.authToken.token ?? ''
  }

  /**
   * Download parent letters for given student ids.
   */
  downloadParentLettersForStudents(params: ParentLetterParams) {
    const url = join(config.readingEggsV1Url, 'teacher_ui', 'students/download_parent_letters').href
    return this.createDownloadRequest(url, params, 'parent-letters-students', 'application/pdf')
  }

  /**
   * Download parent letters for given teacher ids
   */
  downloadParentLettersForTeachers(teacherIds: string[], subscriptionType: SubscriptionType) {
    const params = { teacher_ids: teacherIds, subscription_type: subscriptionType }
    const url = join(config.readingEggsV1Url, 'teacher_ui', 'teachers/download_parent_letters').href
    return this.createDownloadRequest(url, params, 'parent-letters-teachers', 'application/zip')
  }

  /**
   * Initiates a file download
   */
  async createDownloadRequest(
    url: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params: Record<string, any>,
    fileName: string,
    mimetype: string,
    successStatusCodes: number[] = [200, 201],
  ) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: this.Authorization,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    })

    if (successStatusCodes.includes(response.status)) {
      try {
        const blob = await response.blob()
        this.downloadBlobToFile(blob, { prefix: fileName, mimetype, timestamp: new Date() })
        return true
      } catch (error: unknown) {
        throw new DownloadFileError(url, response, error)
      }
    } else {
      throw new DownloadFileError(url, response)
    }
  }

  downloadBlobToFile(blob: Blob, options: FileOptions) {
    const extension = options.mimetype.split('/')[1]
    const formattedTimestamp = options.timestamp
      ? `-${format(options.timestamp, options.timestampFormat ?? 'yyyy-MM-dd')}`
      : ''
    this.downloadjs(blob, `${options.prefix}${formattedTimestamp}.${extension}`, options.mimetype)
  }
}

declare module '@ember/service' {
  interface Registry {
    'download-file': DownloadFileService
  }
}
